import { useEffect, useState } from "react";
import { Eye, Mail, PencilLine, Save } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const STATUS_LABELS = { sent: "Envoyé", failed: "Échec", queued: "En file", skipped_disabled: "Désactivé", skipped_duplicate: "Doublon évité" };

export default function EmailsAdminPage() {
  const [tab, setTab] = useState("reglages");
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ type: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ subject: "", html: "" });
  const [preview, setPreview] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");

  const loadSettings = () => api.get("/admin/emails/settings").then((r) => { setSettings(r.data); setAdminEmail(r.data.admin_notification_email || ""); });
  const loadTemplates = () => api.get("/admin/emails/templates").then((r) => setTemplates(r.data));
  const loadLogs = () => api.get("/admin/emails/logs", { params: Object.fromEntries(Object.entries(logFilter).filter(([, v]) => v)) }).then((r) => setLogs(r.data));
  useEffect(() => { loadSettings(); loadTemplates(); }, []);
  useEffect(() => { if (tab === "historique") loadLogs(); }, [tab, logFilter]);

  const toggle = async (key, enabled) => {
    try {
      await api.patch("/admin/emails/settings", { toggles: { [key]: enabled } });
      toast.success(`E-mail « ${key} » ${enabled ? "activé" : "désactivé"}`);
      loadSettings();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAdminEmail = async () => {
    try {
      await api.patch("/admin/emails/settings", { admin_notification_email: adminEmail });
      toast.success("Adresse de notifications admin enregistrée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (t) => { setEditing(t); setForm({ subject: t.subject, html: t.html }); setPreview(null); };
  const saveTemplate = async () => {
    try {
      await api.put(`/admin/emails/templates/${editing.key}`, form);
      toast.success("Modèle enregistré (nouvelle version)");
      setEditing(null);
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const previewTemplate = async () => {
    try {
      const { data } = await api.post(`/admin/emails/templates/${editing.key}/preview`, form);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="emails-loading">Chargement…</div>;

  return (
    <div data-testid="admin-emails-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">LOT 6A — E-mails transactionnels</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">E-mails</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Mode <strong>{settings.mode === "production" ? "PRODUCTION" : "TEST"}</strong> — fournisseur : {settings.provider}.
            {settings.mode !== "production" && <> Tous les envois sont redirigés vers <strong>{settings.test_redirect}</strong>.</>}
          </p>
        </div>
        <div className="flex gap-2" role="tablist">
          {[["reglages", "Activation"], ["modeles", "Modèles"], ["historique", "Historique"]].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} data-testid={`emails-tab-${key}`}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === key ? "bg-deepgreen text-white" : "border border-border text-deepgreen hover:bg-sage-light"}`}>{label}</button>
          ))}
        </div>
      </div>

      {tab === "reglages" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="card-ae p-6 lg:col-span-2" data-testid="emails-toggles">
            <h2 className="font-heading text-lg text-deepgreen">Activation par type d'e-mail</h2>
            <p className="mt-1 text-xs text-muted-foreground">Les e-mails de sécurité (réinitialisation de mot de passe) ne peuvent pas être désactivés.</p>
            <div className="mt-4 divide-y divide-border">
              {settings.types.map((t) => (
                <label key={t.key} className="flex items-center justify-between gap-4 py-2.5" data-testid={`email-toggle-row-${t.key}`}>
                  <span className="text-sm"><span className="font-semibold text-deepgreen">{t.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{t.audience === "admin" ? "→ Art Event" : "→ client"}</span></span>
                  {t.toggleable ? (
                    <input type="checkbox" checked={t.enabled} onChange={(e) => toggle(t.key, e.target.checked)}
                      className="h-4 w-4 accent-[#2C4C3B]" data-testid={`email-toggle-${t.key}`} />
                  ) : (
                    <span className="rounded-full bg-sage-light px-2 py-0.5 text-[10px] font-bold text-deepgreen" data-testid={`email-toggle-locked-${t.key}`}>TOUJOURS ACTIF</span>
                  )}
                </label>
              ))}
            </div>
          </div>
          <div className="card-ae p-6" data-testid="emails-settings-side">
            <h2 className="font-heading text-lg text-deepgreen">Notifications Art Event</h2>
            <label className="label-ae mt-4" htmlFor="admin-notif-email">Adresse de réception des alertes admin</label>
            <input id="admin-notif-email" className="input-ae" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} data-testid="emails-admin-address" />
            <button type="button" onClick={saveAdminEmail} className="btn-primary-ae mt-3 w-full !py-2.5 !text-xs" data-testid="emails-admin-address-save"><Save className="h-4 w-4" /> Enregistrer</button>
            <div className="mt-5 rounded-lg bg-cream p-3 text-xs text-muted-foreground">
              <p><strong>Expéditeur :</strong> {settings.from_name} — Reply-To {settings.reply_to}</p>
              <p className="mt-1"><strong>Rappel RDV J-1 :</strong> planifié chaque jour (Europe/Paris), idempotent.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "modeles" && (
        <div className="card-ae mt-6 overflow-x-auto" data-testid="emails-templates">
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">Modèle</th><th className="px-5 py-3">Sujet</th><th className="px-5 py-3">Version</th><th className="px-5 py-3">Source</th><th className="px-5 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {templates.map((t) => (
                <tr key={t.key} className="transition-colors hover:bg-cream/60" data-testid={`email-template-row-${t.key}`}>
                  <td className="px-5 py-3"><p className="font-semibold text-deepgreen">{t.label}</p><p className="font-mono text-xs text-muted-foreground">{t.key}</p></td>
                  <td className="px-5 py-3 text-muted-foreground">{t.subject}</td>
                  <td className="px-5 py-3">v{t.version}</td>
                  <td className="px-5 py-3">{t.custom ? <StatusBadge status="Personnalisé" /> : <StatusBadge status="Standard" />}</td>
                  <td className="px-5 py-3 text-right">
                    <button type="button" onClick={() => openEdit(t)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`email-template-edit-${t.key}`}><PencilLine className="mr-1 inline h-3.5 w-3.5" />Modifier</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "historique" && (
        <div className="mt-6" data-testid="emails-logs">
          <div className="mb-3 flex flex-wrap gap-2">
            <select className="input-ae !w-56" value={logFilter.status} onChange={(e) => setLogFilter({ ...logFilter, status: e.target.value })} data-testid="emails-log-filter-status">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="input-ae !w-72" value={logFilter.type} onChange={(e) => setLogFilter({ ...logFilter, type: e.target.value })} data-testid="emails-log-filter-type">
              <option value="">Tous les types</option>
              {settings.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="card-ae overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm" data-testid="emails-logs-table">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Destinataire</th><th className="px-5 py-3">Dossier</th><th className="px-5 py-3">Statut</th><th className="px-5 py-3">ID fournisseur</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`email-log-${l.id}`}>
                    <td className="px-5 py-2.5 text-xs text-muted-foreground">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-5 py-2.5 font-mono text-xs">{l.type}</td>
                    <td className="px-5 py-2.5 text-xs">{l.recipient_original}{l.test_mode && <span className="ml-1 rounded bg-gold/20 px-1 text-[10px] font-bold">TEST</span>}</td>
                    <td className="px-5 py-2.5 font-mono text-xs text-muted-foreground">{l.entity_type || "—"}</td>
                    <td className="px-5 py-2.5"><StatusBadge status={STATUS_LABELS[l.status] || l.status} /></td>
                    <td className="px-5 py-2.5 font-mono text-xs text-muted-foreground">{l.provider_message_id || l.error_class || "—"}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-muted-foreground">Aucun envoi enregistré.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={Boolean(editing)} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-3xl bg-cream" data-testid="email-template-dialog">
          {editing && <>
            <DialogHeader>
              <DialogTitle className="font-heading text-deepgreen"><Mail className="mr-2 inline h-5 w-5" />{editing.label}</DialogTitle>
              <DialogDescription>Placeholders : {"{{ prenom }}"}, {"{{ ref }}"}, {"{{ lien }}"}, {"{{ total }}"}… — aperçu avec données d'exemple.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><label className="label-ae" htmlFor="tpl-subject">Sujet</label><input id="tpl-subject" className="input-ae" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-template-subject" /></div>
              <div><label className="label-ae" htmlFor="tpl-html">Contenu HTML</label><textarea id="tpl-html" className="input-ae min-h-44 font-mono text-xs" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} data-testid="email-template-html" /></div>
              <div className="flex gap-2">
                <button type="button" onClick={previewTemplate} className="btn-secondary-ae !px-5 !py-2.5 !text-xs" data-testid="email-template-preview-btn"><Eye className="h-4 w-4" /> Aperçu</button>
                <button type="button" onClick={saveTemplate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="email-template-save"><Save className="h-4 w-4" /> Enregistrer</button>
              </div>
              {preview && (
                <div className="rounded-lg border border-border bg-white" data-testid="email-template-preview">
                  <p className="border-b border-border px-3 py-2 text-xs font-semibold text-deepgreen">Sujet : {preview.subject}</p>
                  <iframe title="Aperçu e-mail" srcDoc={preview.html} sandbox="" className="h-80 w-full" />
                </div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
